import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { PrimeReactProvider } from "primereact/api";
import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  BrowserRouter,
  useNavigate,
  Navigate,
} from "react-router-dom";
import "./App.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { io } from "socket.io-client";
import Layout from "./components/Layout";
import { isAdminUser } from "./utils/auth";
import ScrollToTop from "./components/ScrollToTop";

// Pages & Components
import MainDash from "./pages/MainDash/MainDash";
import ProductAdd from "./pages/Product/Add";
import ProductList from "./pages/Product/List";
import ProductBulkUpload from "./pages/Product/BulkUpload";
import ComboList from "./pages/Combo/ComboList";
import ComboAdd from "./pages/Combo/ComboAdd";
import Sidebar from "./components/Sidebar";
import Banner from "./pages/Banner";
import CatSearch from "./pages/category/CatSearch";
import CatAdd from "./pages/category/CatAdd";
import Report from "./pages/Product/Availability";
import SubcatSearch from "./pages/subcat/SubcatSearch";
import SubcatAdd from "./pages/subcat/SubcatAdd";
import Price from "./pages/PriceUpdate";
import Login from "./pages/Auth/Login";
import NewOrderDetails from "./pages/Order/NewOrderDetails";
import OrdersHistory from "./components/OrderManagement/OrdersHistory";
import CustomerReport from "./components/OrderManagement/CustomerReport";
import ProductReport from "./components/OrderManagement/ProductReport";
import Dinein from "./pages/Order/Dine-in";
import Takeaway from "./pages/Takeaway";
import TableBookingPage from "./pages/TableBooking/Tablebooking";
import AddUser from "./pages/UserManagement";
import QrBookingPage from "./pages/QRBooking/QrOrder";
import Setting from "./pages/Setting";
import Configure from "./pages/Configure";
import UserFeedback from "./pages/userfeedback/userfeedback";
import { setLogoutCallback } from "./utils/api";
import ChangePassword from "./pages/Auth/ChangePassword";
import { RestaurantDetailsProvider } from "./context/RestaurantDetailsContext";
import socket from "./socket";
import { vibrateWaiterAlert } from "./utils/vibration";
import OtherAppsSale from "./pages/OtherAppsSale";
import OtherAppSaleHistory from "./pages/OtherAppsSale/OtherAppSaleHistory";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, "+")
    .replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

const publicVapidKey = process.env.REACT_APP_PUBLIC_VAPID_KEY;

async function subscribeToPush() {
  try {
    const userId = localStorage.getItem("userId");
    const role = localStorage.getItem("role");

    // ❗ Required for backend targeting
    if (!userId || !role) return;

    if ("serviceWorker" in navigator && "PushManager" in window) {
      await navigator.serviceWorker.register("/sw.js");
      const activeRegistration = await navigator.serviceWorker.ready;

      const permission = await Notification.requestPermission();
      if (permission !== "granted") return;

      const subscription = await activeRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicVapidKey),
      });

      await fetch(
        `${process.env.REACT_APP_API_BASE}/api/notifications/subscribe`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subscription,
            userId,
            role,
          }),
        },
      );
    }
  } catch (err) {
    console.error("❌ Push subscription error:", err);
  }
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("token"),
  );
  const [canPlayAudio, setCanPlayAudio] = useState(false);
  const username = localStorage.getItem("username");
  const navigate = useNavigate();

  //  Logout callback setup
  useEffect(() => {
    setLogoutCallback(() => {
      setIsAuthenticated(false);
      navigate("/login");
    });
  }, [navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      // console.log("⛔ Socket not started: user not authenticated");
      return;
    }

    const role = localStorage.getItem("role");
    const userId = localStorage.getItem("userId");

    // console.log("👤 Socket init check → role:", role, "userId:", userId);

    if (role !== "waiter" || !userId) {
      // console.log("⛔ Not a waiter or userId missing → skipping waiter socket");
      return;
    }

    const onConnect = () => {
      // console.log("🟢 Waiter socket connected:", socket.id);
      // console.log("➡️ Joining waiter room:", `waiter:${userId}`);
      socket.emit("join-waiter", { userId });
    };

    const onWaiterCalled = (data) => {
      // console.log("🔔 waiter-called event received:", data);

      toast.warning(`🔔 Table ${data.tableNumber} is calling you`, {
        position: "top-right",
        autoClose: 5000,
      });

      // 📳 Vibrate Phone
      vibrateWaiterAlert();
    };

    socket.on("connect", onConnect);
    socket.on("waiter-called", onWaiterCalled);

    return () => {
      socket.off("connect", onConnect);
      socket.off("waiter-called", onWaiterCalled);
    };
  }, [isAuthenticated]);

  const getDefaultRoute = () => {
    const role = localStorage.getItem("role");
    if (role === "waiter") {
      return "/table-booking";
    }
    if (role === "fingliderAdmin") {
      return "/configure";
    }
    return "/dashboard";
  };

  //  Handle successful login
  const handleLoginSuccess = async (token) => {
    // console.log("✅ handleLoginSuccess triggered");

    setIsAuthenticated(true);
    localStorage.setItem("token", token);

    await subscribeToPush();

    navigate(getDefaultRoute(), { replace: true });
  };



  useEffect(() => {
    const enableAudio = () => setCanPlayAudio(true);
    window.addEventListener("click", enableAudio, { once: true });
    window.addEventListener("keydown", enableAudio, { once: true });

    return () => {
      window.removeEventListener("click", enableAudio);
      window.removeEventListener("keydown", enableAudio);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !isAdminUser()) return;

    const onNewOrder = (order) => {
      // console.log("🧾 new-order received:", order);

      toast.info(`🚨 New Order Arrived! Order ID: ${order.orderId}`, {
        position: "top-right",
        autoClose: 5000,
      });

      if (canPlayAudio) {
        const audio = new Audio("/audio/notification.mp3");
        audio.play().catch((err) => console.log("Audio blocked:", err));
      }
    };

    socket.on("new-order", onNewOrder);

    return () => {
      socket.off("new-order", onNewOrder);
    };
  }, [isAuthenticated, canPlayAudio]);

  // useEffect(() => {
  //   if (isAuthenticated && isAdminUser()) {
  //     subscribeToPush();
  //   }
  // }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route
          path="/"
          element={<Login onLoginSuccess={handleLoginSuccess} />}
        />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    );
  }

  if (localStorage.getItem("mustChangePassword") === "true") {
    return (
      <Routes>
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="*" element={<Navigate to="/change-password" replace />} />
      </Routes>
    );
  }

  // Determine default route based on user role


  return (
    <div className="App">
      <ToastContainer position="top-center" autoClose={3000} />
      {/* <div className="AppGlass">
        {isAuthenticated && <Sidebar setIsAuthenticated={setIsAuthenticated} />} */}
      <Routes>
        <Route path="/" element={<Navigate to={getDefaultRoute()} />} />
        {/* <Route path="/dashboard" element={<MainDash username={username} />} />
          <Route path="/product" element={<ProductList />} />
          <Route path="/product/productdetail/:id" element={<ProductAdd />} />
          <Route path="/product/add" element={<ProductAdd />} />
          <Route path="/price-update" element={<Price />} />
          <Route path="/categories" element={<CatSearch />} />
          <Route path="/add-category" element={<CatAdd />} />
          <Route path="/subcategories" element={<SubcatSearch />} />
          <Route path="/add-subcategory" element={<SubcatAdd />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/dining" element={<Dinein />} />
          <Route path="/banner" element={<Banner />} />
          <Route path="/reports" element={<Report />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/orderdetails" element={<NewOrderDetails />} />
          <Route path="/orders-history" element={<OrdersHistory />} /> */}
        {/* Single shared Layout — never remounts on navigation */}
        <Route element={<Layout setIsAuthenticated={setIsAuthenticated} hideHeader={true} />}>
          <Route
            path="/dashboard"
            element={
              isAdminUser() ? (
                <MainDash username={username} />
              ) : localStorage.getItem("role") === "fingliderAdmin" ? (
                <Navigate to="/configure" replace />
              ) : (
                <Navigate to="/table-booking" replace />
              )
            }
          />
          <Route path="/product" element={<ProductList />} />
          <Route path="/product/add" element={<ProductAdd />} />
          <Route path="/product/productdetail/:id" element={<ProductAdd />} />
          <Route path="/product/bulk-upload" element={<ProductBulkUpload />} />
          <Route path="/combos" element={<ComboList />} />
          <Route path="/combos/add" element={<ComboAdd />} />
          <Route path="/combos/edit/:id" element={<ComboAdd />} />
          <Route path="/price-update" element={<Price />} />
          <Route path="/categories" element={<CatSearch />} />
          <Route path="/add-category" element={<CatAdd />} />
          <Route path="/subcategories" element={<SubcatSearch />} />
          <Route path="/add-subcategory" element={<SubcatAdd />} />
          <Route path="/feedback" element={<UserFeedback />} />
          <Route path="/orderdetails" element={<NewOrderDetails />} />
          <Route path="/orders-history" element={<OrdersHistory key="orders-history" />} />
          <Route path="/customer-report" element={<CustomerReport />} />
          <Route path="/product-report" element={<ProductReport />} />
          <Route path="/report-other-app-sale" element={<OrdersHistory key="other-apps-sale" defaultToSummary={true} isSummaryPage={true} />} />
          <Route path="/dining" element={<Dinein />} />
          <Route path="/banner" element={<Banner />} />
          <Route path="/other-apps-sale" element={<OtherAppsSale />} />
          <Route path="/other-apps-sale/app/:appId" element={<OtherAppSaleHistory />} />
          <Route path="/setting" element={<Setting />} />
          <Route path="/configure" element={<Configure />} />
          <Route path="/reports" element={<Report />} />
          <Route path="/takeaway" element={<Takeaway />} />
          <Route path="/table-booking" element={<TableBookingPage />} />
          <Route path="/addusers" element={<AddUser />} />
          <Route path="/qrordering" element={<QrBookingPage />} />
        </Route>
      </Routes>
      {/* </div> */}
    </div>
  );
}

function AppWrapper() {
  return (
    <PrimeReactProvider>
      <BrowserRouter>
        <ScrollToTop />
        <RestaurantDetailsProvider>
          <App />
        </RestaurantDetailsProvider>
      </BrowserRouter>
    </PrimeReactProvider>
  );
}

export default AppWrapper;
